import axios from "axios";
import cn from "classnames";
import DOMPurify from "dompurify";
import { ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import {
  buildPreferenceFields as buildSharedPreferenceFields,
  flattenPreferenceFields,
  isFieldValueSet,
  parseDescription,
  PreferenceField,
  PreferenceMapping,
} from "../../common/preferenceFields";
import { NewReportPreferences, reportPreferences } from "../../db/schema";
import Button from "../common/Button";
import Loading from "../common/Loading";
import ConditionalFieldGroup from "./ConditionalFieldGroup";
import Dropdown from "./Dropdown";
import ListInput from "./ListInput";
import TextArea from "./TextArea";
import TextInput from "./TextInput";
import Toggle from "./Toggle";
import { TooltipWrapper } from "./TooltipWrapper";

// Sanitization utilities
const sanitizeInput = (value: string): string => {
  return DOMPurify.sanitize(value);
};

const sanitizeTextArea = (value: string): string => {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [], // Strip all HTML tags
    ALLOWED_ATTR: [], // Strip all attributes
  });
};

export type {
  PreferenceField,
  PreferenceFieldGroup,
  PreferenceEntry,
  PreferenceMapping,
} from "../../common/preferenceFields";

/**
 * Renders a shared field description. Definitions live in
 * `common/preferenceFields.ts` (synced from server/) and are plain strings so
 * they can also be consumed by the mobile app, with `**text**` marking
 * emphasis — this turns those runs into <b> for the web.
 */
export const PreferenceDescription = ({
  description,
}: {
  description: string;
}) => (
  <p>
    {parseDescription(description).map((run, i) =>
      run.bold ? <b key={i}>{run.text}</b> : <span key={i}>{run.text}</span>
    )}
  </p>
);

interface PreferencesFormProps {
  entityName?: string;
  /** Officer's badge number — used in the call sign format examples */
  entityBadge?: string;
  entityId?: string;
  entityType: "user" | "agency";
  saveEndpoint: string;
  fetchEndpoint: string;
  customButtonText?: string;
  children?: ReactNode;
  onAfterSave?: () => Promise<void>;
  /** External dirty flag (e.g., from template editor changes) */
  externalDirty?: boolean;
  /**
   * Hide the per-field "push to all officers" icons. Used when editing agency
   * preferences from the agency management portal, where the push endpoints
   * (which require an agency admin session) are unavailable.
   */
  hidePushIcons?: boolean;
  /** Called with the saved preferences (the officer view keeps AuthContext in step) */
  onPreferencesSaved?: (preferences: Partial<NewReportPreferences>) => void;
}

/**
 * Signature-compatible wrapper over the shared, platform-agnostic definitions
 * in `common/preferenceFields.ts`. That file is the single source of truth for
 * labels, descriptions, ordering, options, and grouping, and is synced to the
 * mobile app by `yarn sync-preferences` in server/ — so a preference added
 * there shows up on web and iOS identically. Do not add fields here.
 */
export const buildPreferenceFields = (
  entityName?: string,
  entityType?: string,
  currentPrefs?: Partial<NewReportPreferences>,
  entityBadge?: string
): PreferenceMapping =>
  buildSharedPreferenceFields({
    entityName,
    entityType,
    currentPrefs,
    entityBadge,
  });

const PreferencesForm: React.FC<PreferencesFormProps> = ({
  entityName,
  entityBadge,
  entityId,
  entityType,
  saveEndpoint,
  fetchEndpoint,
  customButtonText,
  children,
  onAfterSave,
  externalDirty = false,
  hidePushIcons = false,
  onPreferencesSaved,
}) => {
  const [tempPreferences, setTempPreferences] = useState<
    Partial<NewReportPreferences>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [highlightStatutes, setHighlightStatutes] = useState(false);
  const [agencyPreferences, setAgencyPreferences] = useState<
    Partial<NewReportPreferences>
  >({});
  const [hasAgency, setHasAgency] = useState(false);
  const [, setIsLoadingAgencyPreference] = useState(false);
  const [pushFieldKey, setPushFieldKey] = useState<string | null>(null);
  const [isPushing, setIsPushing] = useState(false);
  const location = useLocation();
  const markSavedRef = useRef<() => void>(() => {});

  const { markSaved } = useUnsavedChanges({
    currentState: { tempPreferences, externalDirty },
    initialState: { tempPreferences, externalDirty: false },
    onCancel: () => {},
  });
  markSavedRef.current = markSaved;

  const PREFERENCE_FIELDS = buildPreferenceFields(
    entityName,
    entityType,
    tempPreferences,
    entityBadge
  );
  // Key -> field lookup including group controllers and children
  const FLAT_FIELDS = flattenPreferenceFields(PREFERENCE_FIELDS);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("highlight") === "statutes") {
      setHighlightStatutes(true);
      setTimeout(() => setHighlightStatutes(false), 2000);
    }
  }, [location]);

  useEffect(() => {
    const fetchPreferences = async () => {
      if (entityType === "agency" && !entityId) {
        setIsLoading(false);
        return;
      }

      try {
        const { data } = await axios.get(fetchEndpoint);
        setTempPreferences(data.preferences);

        // Check if user has an agency_id
        if (entityType === "user" && data.preferences?.userId) {
          try {
            // Fetch user info to check if they belong to an agency
            const userResponse = await axios.get("/api/auth/user");

            if (userResponse.data.user?.agency_id) {
              setHasAgency(true);

              // Pre-fetch agency preferences
              setIsLoadingAgencyPreference(true);
              const agencyResponse = await axios.get(
                `/api/agency/report-preferences`
              );
              if (agencyResponse.data.preferences) {
                setAgencyPreferences(agencyResponse.data.preferences);
              }
              setIsLoadingAgencyPreference(false);
            }
          } catch (err) {
            console.error("Error fetching user agency info:", err);
          }
        }
      } catch (error) {
        toast.error("Failed to load preferences");
        console.error("Error loading preferences:", error);
      } finally {
        setIsLoading(false);
        // Set baseline after fetch so unsaved changes detection starts clean
        setTimeout(() => markSavedRef.current(), 0);
      }
    };

    fetchPreferences();
  }, [entityId, entityType, fetchEndpoint]);

  const handlePreferenceChange = (key: string, value: any) => {
    let sanitizedValue = value;
    if (typeof value === "string") {
      sanitizedValue =
        FLAT_FIELDS[key].type === "textarea"
          ? sanitizeTextArea(value)
          : sanitizeInput(value);
    } else if (
      Array.isArray(value) &&
      value.every((item) => typeof item === "string")
    ) {
      // For list type fields, sanitize each string in the array
      sanitizedValue = value.map((item) => sanitizeInput(item));
    }
    setTempPreferences((prev) => ({ ...prev, [key]: sanitizedValue }));
  };

  // Function to apply agency setting for a specific preference
  const applyAgencySetting = (key: string) => {
    if (!hasAgency || !agencyPreferences || !(key in agencyPreferences)) {
      return;
    }

    const agencyValue = agencyPreferences[key];
    setTempPreferences((prev) => ({ ...prev, [key]: agencyValue }));
    toast.success(`Applied agency setting for ${FLAT_FIELDS[key].label}`);
  };

  // Function to push a single preference to all officers
  const handlePushField = async () => {
    if (!pushFieldKey) return;

    setIsPushing(true);
    try {
      const { data } = await axios.post("/api/agency/push-preference", {
        field: pushFieldKey,
        value: tempPreferences[pushFieldKey],
      });
      toast.success(data.message);
    } catch (error) {
      toast.error(
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : "Failed to push setting"
      );
    } finally {
      setIsPushing(false);
      setPushFieldKey(null);
    }
  };

  const handleSave = async () => {
    if (entityType === "agency" && !entityId) {
      toast.error(
        `${
          entityType.charAt(0).toUpperCase() + entityType.slice(1)
        } information not available`
      );
      return;
    }

    setIsSaving(true);
    try {
      // Set entity type if needed
      // Exclude default_template_id — it's managed by the toggle-default endpoint
      const { default_template_id, ...prefsWithoutDefault } = tempPreferences;
      const updatedPreferences = {
        ...prefsWithoutDefault,
        ...(entityType === "agency" && { entity_type: "agency" }),
      };

      const { data } = await axios.patch(saveEndpoint, updatedPreferences);
      await onAfterSave?.();
      toast.success(
        `${
          entityType.charAt(0).toUpperCase() + entityType.slice(1)
        } preferences saved successfully`
      );
      setTempPreferences(data.preferences);
      onPreferencesSaved?.(data.preferences);
      setTimeout(() => markSavedRef.current(), 0);
    } catch (error) {
      toast.error(
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : "Failed to save preferences"
      );
      console.error("Error saving preferences:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  const renderFieldControl = (key: string, field: PreferenceField) => {
    const showAgencyIcon =
      entityType === "user" && hasAgency && key in agencyPreferences;
    const showPushIcon = entityType === "agency" && !hidePushIcons;

    const label = (
      <TooltipWrapper
        label={field.label}
        description={<PreferenceDescription description={field.description} />}
        showAgencyIcon={showAgencyIcon}
        onAgencyIconClick={() => applyAgencySetting(key)}
        showPushIcon={showPushIcon}
        onPushIconClick={() => setPushFieldKey(key)}
      />
    );

    switch (field.type) {
      case "toggle":
        return (
          <Toggle
            label={label}
            checked={tempPreferences[key]}
            onChange={(e) => handlePreferenceChange(key, e.target.checked)}
          />
        );
      case "dropdown":
        return (
          <Dropdown
            label={label}
            options={field.options || []}
            value={tempPreferences[key]}
            onChange={(value) => handlePreferenceChange(key, value)}
            size="sm"
          />
        );
      case "textarea":
        return (
          <TextArea
            label={label}
            value={tempPreferences[key] || ""}
            onChange={(e) => handlePreferenceChange(key, e.target.value)}
            rows={4}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
          />
        );
      case "textinput":
        return (
          <TextInput
            label={label}
            action={`Set ${field.label.replace(/^Include /, "")}`}
            value={tempPreferences[key] || ""}
            onSave={(value) => handlePreferenceChange(key, value)}
            placeholder={field.placeholder}
            size="sm"
          />
        );
      case "list":
        return (
          <ListInput
            label={label}
            items={tempPreferences[key] || []}
            onItemsChange={(items) => handlePreferenceChange(key, items)}
            placeholder={field.placeholder}
            maxItems={field.maxItems}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="grid gap-y-2" data-tour="preferences-form">
      {Object.entries(PREFERENCE_FIELDS).map(([key, entry]) => {
        const isStatutesField = key === reportPreferences.statute_state.name;

        return (
          <div
            key={key}
            data-tour="preference-field"
            data-pref={key}
            className={cn(
              isStatutesField &&
                highlightStatutes &&
                "animate-flashing text-blue-500 dark:text-blue-300"
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex-grow">
                {entry.type === "group" ? (
                  <>
                    {renderFieldControl(key, entry.controller)}
                    <ConditionalFieldGroup
                      visible={isFieldValueSet(tempPreferences[key])}
                    >
                      {Object.entries(entry.children).map(
                        ([childKey, childField]) => (
                          <div key={childKey} data-pref={childKey}>
                            {renderFieldControl(childKey, childField)}
                          </div>
                        )
                      )}
                    </ConditionalFieldGroup>
                  </>
                ) : (
                  renderFieldControl(key, entry)
                )}
              </div>
            </div>
          </div>
        );
      })}
      {children && <hr className="my-6 border-base-300" />}
      {children}
      <Button
        variant="primary"
        data-tour="preferences-save"
        className="w-full sticky bottom-0 py-4"
        onClick={handleSave}
        disabled={isSaving}
      >
        {customButtonText || "Save Preferences"}
      </Button>

      <dialog
        id="modal-push-field"
        className={`modal ${pushFieldKey ? "modal-open" : "pointer-events-none"}`}
      >
        <div className="modal-box max-w-96">
          <h3 className="font-bold text-lg mt-2">
            Push "{pushFieldKey && FLAT_FIELDS[pushFieldKey]?.label}" to
            All Officers
          </h3>
          <p className="py-4 text-gray-600 dark:text-base-content/70">
            Are you sure you want to push this setting to all officers? This
            will override their individual preference and cannot be undone.
          </p>
          <div className="modal-action mt-2">
            <Button
              variant="outline"
              onClick={() => setPushFieldKey(null)}
              disabled={isPushing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePushField}
              disabled={isPushing}
            >
              {isPushing ? "Pushing..." : "Confirm"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setPushFieldKey(null)}>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default PreferencesForm;
